import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { CalendarDays, Copy, Download, Mail, Paperclip, Phone, Search } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { api } from "@/lib/api";
import { LOCATIONS, WORK_TYPES } from "@/data/site";
import { fmtDate, isOverdue, Lead, STATUSES } from "./types";
import StatusBadge from "./StatusBadge";
import type { ShellCtx } from "./CrmShell";

type Row = { label: string; leads: number; won: number };
type Stats = {
  total: number; by_status: Record<string, number>; overdue_follow_ups: number; unassigned: number; win_rate: number;
  site_visits_requested: number; by_page: { page: string; leads: number; won: number }[];
  by_location: { location: string; leads: number; won: number }[];
};
const EMPTY = { q: "", status: "", work_type: "", location: "", owner: "", follow_up: "", duplicates: "" };

function Breakdown({ title, rows, testid, onPick }: { title: string; rows: Row[]; testid: string; onPick?: (label: string) => void }) {
  if (!rows.length) return null;
  const max = Math.max(...rows.map((p) => p.leads));
  return (
    <details open className="border border-slate-800 bg-[#161920] px-4 py-3" data-testid={testid}>
      <summary className="cursor-pointer font-mono text-[10px] uppercase text-slate-400">{title}</summary>
      <ul className="mt-3 space-y-1.5">
        {rows.map((p) => (
          <li key={p.label} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 text-xs" data-testid={`${testid}-row`}>
            <button type="button" disabled={!onPick || p.label.startsWith("(")} onClick={() => onPick?.(p.label)}
              className="relative h-6 overflow-hidden bg-[#0F1115] text-left enabled:hover:ring-1 enabled:hover:ring-brand-500">
              <div className="absolute inset-y-0 left-0 bg-brand-500/25" style={{ width: `${(p.leads / max) * 100}%` }} />
              <span className="relative truncate px-2 leading-6 text-slate-200">{p.label}</span>
            </button>
            <span className="font-mono text-slate-400">{p.leads} leads · {p.won} won</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function Kpis({ s }: { s: Stats | null }) {
  const items = [
    ["Total leads", s?.total], ["New", s?.by_status.new], ["Unassigned", s?.unassigned],
    ["Overdue follow-ups", s?.overdue_follow_ups], ["Site visits", s?.site_visits_requested], ["Quoted", s?.by_status.quoted], ["Win rate", s ? `${s.win_rate}%` : undefined],
  ];
  return (
    <div className="grid grid-cols-3 gap-px bg-slate-800 sm:grid-cols-4 lg:grid-cols-7" data-testid="crm-kpis">
      {items.map(([label, v]) => (
        <div key={label as string} className="bg-[#161920] px-4 py-3">
          <div className="font-mono text-[10px] uppercase text-slate-500">{label}</div>
          <div className={`font-display text-2xl font-bold ${label === "Overdue follow-ups" && Number(v) > 0 ? "text-red-400" : "text-white"}`} data-testid={`kpi-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>{v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { staff } = useOutletContext<ShellCtx>();
  const [filters, setFilters] = useState(EMPTY);
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [exporting, setExporting] = useState(false);
  const { user } = useAuth();
  const sendReport = async () => {
    try {
      const { data } = await api.post("/reports/monthly/send");
      toast.success(`Report for ${data.month} (month to date) sent to ${data.to}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/leads/export", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setExporting(false);
    }
  };
  const ownerName = (id: string | null) => staff.find((s) => s.id === id)?.name || "—";
  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setFilters({ ...filters, [k]: e.target.value });

  useEffect(() => { api.get("/leads/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/leads", { params: filters }).then((r) => setLeads(r.data)), 250);
    return () => clearTimeout(t);
  }, [filters]);

  const sel = (k: keyof typeof EMPTY, label: string, opts: [string, string][]) => (
    <select className="crm-field" value={filters[k]} onChange={set(k)} data-testid={`filter-${k}`}>
      <option value="">{label}</option>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="crm-dashboard">
      <Kpis s={stats} />
      {stats && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Breakdown title="Leads by suburb (click to filter)" testid="crm-suburbs" rows={stats.by_location.map((r) => ({ label: r.location, leads: r.leads, won: r.won }))}
            onPick={(label) => setFilters({ ...filters, location: label })} />
          <Breakdown title="Leads by landing page" testid="crm-source-pages" rows={stats.by_page.map((r) => ({ label: r.page, leads: r.leads, won: r.won }))} />
        </div>
      )}
      <div className="grid gap-2 md:grid-cols-4 xl:grid-cols-8">
        <div className="relative md:col-span-4 xl:col-span-2">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <input className="crm-field pl-8" placeholder="Search name, company, phone, email…" value={filters.q} onChange={set("q")} data-testid="filter-search" />
        </div>
        {sel("status", "All statuses", STATUSES.map((s) => [s, s[0].toUpperCase() + s.slice(1)]))}
        {sel("work_type", "All services", WORK_TYPES.map((w) => [w, w]))}
        <input className="crm-field" list="crm-location-options" placeholder="Any location" value={filters.location} onChange={set("location")} data-testid="filter-location" />
        <datalist id="crm-location-options">{LOCATIONS.map((l) => <option key={l} value={l} />)}</datalist>
        {sel("owner", "All owners", [["unassigned", "Unassigned"], ...staff.map((s) => [s.id, s.name] as [string, string])])}
        {sel("follow_up", "Any follow-up", [["overdue", "Overdue"], ["today", "Due today"], ["upcoming", "Upcoming"], ["none", "None scheduled"]])}
        {sel("duplicates", "Incl. duplicates", [["hide", "Hide duplicates"], ["only", "Duplicates only"]])}
      </div>
      {JSON.stringify(filters) !== JSON.stringify(EMPTY) && (
        <button className="text-xs text-brand-400 hover:underline" onClick={() => setFilters(EMPTY)} data-testid="filter-clear">Clear filters</button>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500" data-testid="leads-count">{leads ? `${leads.length} lead${leads.length === 1 ? "" : "s"}` : ""}</p>
        <div className="flex items-center gap-2">
          {user && user.role === "admin" && (
            <button onClick={sendReport} data-testid="send-report-button"
              className="flex items-center gap-1.5 rounded-sm border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:border-brand-500 hover:text-brand-400">
              <Mail className="h-3.5 w-3.5" /> Email this month's report
            </button>
          )}
          <button onClick={exportCsv} disabled={exporting} data-testid="export-leads-button"
            className="flex items-center gap-1.5 rounded-sm border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:border-brand-500 hover:text-brand-400 disabled:opacity-50">
            <Download className="h-3.5 w-3.5" /> {exporting ? "Exporting…" : "Export to spreadsheet (CSV)"}
          </button>
        </div>
      </div>
      <LeadTable leads={leads} ownerName={ownerName} />
    </div>
  );
}

function LeadTable({ leads, ownerName }: { leads: Lead[] | null; ownerName: (id: string | null) => string }) {
  if (!leads) return <p className="py-10 text-center text-sm text-slate-500">Loading leads…</p>;
  if (!leads.length) return <p className="py-10 text-center text-sm text-slate-500" data-testid="leads-empty">No leads match these filters.</p>;
  return (
    <>
      <div className="hidden overflow-x-auto border border-slate-800 md:block">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-[#161920] text-left font-mono text-[10px] uppercase text-slate-500">
            <tr>{["Received", "Contact", "Service", "Location", "Phone", "Status", "Owner", "Follow-up"].map((h) => <th key={h} className="px-3 py-2 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {leads.map((l) => (
              <tr key={l.id} className="hover:bg-[#161920]" data-testid={`lead-row-${l.id}`}>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-400">{fmtDate(l.created_at)}</td>
                <td className="px-3 py-2.5">
                  <Link to={`/staff/leads/${l.id}`} className="font-medium text-white hover:text-brand-400" data-testid={`lead-link-${l.id}`}>{l.name}</Link>
                  {l.duplicate_of && <span className="ml-2 inline-flex items-center gap-1 rounded-sm bg-orange-500/15 px-1.5 py-0.5 text-[10px] uppercase text-orange-300" data-testid="lead-duplicate-flag"><Copy className="h-3 w-3" />Dup</span>}
                  {l.site_visit_requested && <span title={`Site visit ${l.site_visit_date} ${l.site_visit_slot}`} className="ml-1 inline-flex items-center rounded-sm bg-brand-500/15 px-1.5 py-0.5 text-brand-300" data-testid="lead-visit-flag"><CalendarDays className="h-3 w-3" /></span>}
                  {l.attachments?.length > 0 && <span title={`${l.attachments.length} plan(s)`} className="ml-1 inline-flex items-center gap-0.5 rounded-sm bg-slate-700/50 px-1.5 py-0.5 text-[10px] text-slate-300" data-testid="lead-files-flag"><Paperclip className="h-3 w-3" />{l.attachments.length}</span>}
                  {l.company && <div className="text-xs text-slate-500">{l.company}</div>}
                </td>
                <td className="px-3 py-2.5 text-slate-300">{l.work_type}</td>
                <td className="px-3 py-2.5 text-slate-400">{l.location || "—"}</td>
                <td className="whitespace-nowrap px-3 py-2.5">{l.phone ? <a href={`tel:${l.phone}`} className="text-slate-300 hover:text-brand-400">{l.phone}</a> : <span className="text-slate-600">—</span>}</td>
                <td className="px-3 py-2.5"><StatusBadge status={l.status} /></td>
                <td className="px-3 py-2.5 text-slate-400">{ownerName(l.owner_id)}</td>
                <td className={`whitespace-nowrap px-3 py-2.5 ${isOverdue(l) ? "text-red-400" : "text-slate-400"}`}>{fmtDate(l.follow_up_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-2 md:hidden">
        {leads.map((l) => (
          <div key={l.id} className="flex items-center justify-between border border-slate-800 bg-[#161920] p-3" data-testid={`lead-card-${l.id}`}>
            <Link to={`/staff/leads/${l.id}`} className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><span className="truncate font-medium text-white">{l.name}</span><StatusBadge status={l.status} /></div>
              <div className="truncate text-xs text-slate-500">{l.work_type} · {l.location || "—"}</div>
              <div className={`text-xs ${isOverdue(l) ? "text-red-400" : "text-slate-500"}`}>Follow-up: {fmtDate(l.follow_up_at)}</div>
            </Link>
            {l.phone && <a href={`tel:${l.phone}`} className="ml-3 grid h-11 w-11 place-items-center bg-brand-500 text-black"><Phone className="h-5 w-5" /></a>}
          </div>
        ))}
      </div>
    </>
  );
}
